import { Box, Button, ButtonGroup, Grid } from "@material-ui/core";
import { DeleteModal } from "containers/delete-modal/DeleteModal";
import FileSaver from "file-saver";
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { RootState } from "state/reducers/AppReducer";
import { FixMeLater } from "types/FixMeLaterType";
import { HeaderBlock } from "../../../components/header-block/HeaderBlock";
import { Loading } from "../../../components/loading/Loading";
import PageContainer from "../../../components/page-container/PageContainer";
import { PagePlaceholder } from "../../../components/page-placeholder/PagePlaceholder";
import Search from "../../../components/search/Search";
import SimpleTable from "../../../components/table/Table";
import FileDropModal from "../../../containers/file-drop-modal/FileDropModal";
import { getIcon } from "../../../icon/icon";
import { deleteFiles, downloadFileBlob, getAllFiles, uploadFile } from "../../../state/actions/FileDownloadActions";
import { getContentType } from "../../../state/constants/ContentType";
import styles from "./ContentPage.module.scss";
import { Header } from "./../../../state/constants/Interfaces/Header"

interface Cell extends Header {
    handleClick?: Function
}

interface FileTableData {
    id: string,
    name: string,
    type: string,
    download: JSX.Element
}

interface ContentFile {
    DisplayName: string,
    FileExtension?: string,
    Active: boolean,
    Type: string,
    Path: string,
    id: string,
    Tags: ContentTags[]
}

interface ContentTags {
    TagName: string
}

interface FileMap {
    [index: string]: ContentFile
}

interface Props extends RouteComponentProps {
}

function ListContentPage(props: Props) {

    const files = useSelector<RootState, FixMeLater>(state => state.FileDownloadReducer.files)

    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()

    const [searchedData, setSearchedData] = useState<FileTableData[]>([])
    const [originalData, setOriginalData] = useState<FileTableData[]>([])
    const [addFileModalOpen, setAddFileModalOpen] = useState(false)
    const [deleteFileModalOpen, setDeleteFileModalOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState([])
    const [formattedSelected, setFormattedSelected] = useState<{ id: string, Name: string }[]>([])

    const setFileModalOpenTrue = () => setAddFileModalOpen(true)
    const setFileModalOpenFalse = () => setAddFileModalOpen(false)
    const setDeleteFileModalOpenTrue = () => {
        setFormattedSelected(fetchFilesById(selected))
        setDeleteFileModalOpen(true)
    }
    const setDeleteFileModalOpenFalse = () => {
        setFormattedSelected([])
        setDeleteFileModalOpen(false)
    }

    const contentTypeList = [getContentType().File, getContentType().Image]

    useEffect(() => {
        setupData()
    }, [])

    useEffect(() => {
        const data: FileTableData[] = createData(files)
        setLoading(false)
        setOriginalData(data)
        setSearchedData(data)
    }, [files])

    function fetchFilesById(fileIds: string[]) {

        if (fileIds != null) {

            const data = fileIds.map(id => {
                const file = files[id]

                if (file != null) {
                    return { id: file.id, Name: file.DisplayName }
                }

                return { id: file.id, Name: t('common.unknown-deleted-data') }
            })

            return data
        }

        return []
    }

    function createData(files: FileMap): FileTableData[] {

        const fileValues = Object.values(files);

        return fileValues.map(value => {
            return {
                id: value.id,
                name: value.DisplayName,
                type: value.Type,
                download: getIcon("arrow-down-circle.svg", styles.downloadImage)
            };
        });
    }

    function setupData() {
        setLoading(true)
        dispatch(getAllFiles(() => {

        }));
    }

    function handleFileUploadDone(file: File[], fileType: string, tags: string[]) {
        const metadata = {
            DisplayName: file[0].name,
            Type: fileType,
            Tags: tags.map(item => ({ TagName: item })) || []
        }
        dispatch(uploadFile(metadata, file[0], () => {
            setupData();
        }));
    }

    function getImageAsync(id: string, callback: (blob: Blob) => void) {
        dispatch(downloadFileBlob(id, (blob: Blob) => {
            if (blob) {
                callback(blob)
            }
        }));
    }

    function downloadFile(id: string) {
        getImageAsync(id, (image) => {
            const blob = image
            const file = files[id]
            if (blob && file) {
                FileSaver.saveAs(blob, file.DisplayName);
            }
        });
    }

    function handleConfirmDelete() {
        // delete by selected
        dispatch(deleteFiles(selected, (success: boolean) => {
            if (success) {
                setSelected([])
            }
            setDeleteFileModalOpenFalse()
        }))

    }

    function handleCancelDelete() {
        setSelected([])
        setDeleteFileModalOpenFalse()
    }

    function createHeaders(): Header[] {
        return [
            {
                id: "name",
                align: "left",
                clickable: true,
                action: (id: string) => props.history.push(`/content/${id}`),
                label: t('targeting-content.name')
            },
            {
                id: "type",
                align: "left",
                label: t('targeting-content.type')
            },
            {
                id: "download",
                clickable: true,
                action: (id: string) => downloadFile(id),
                align: "left",
                label: t('targeting-content.download')
            }
        ];
    }

    function createCells(headers: Header[]) {
        let cells: Cell[] = [];

        headers.forEach(header => {
            let cell: Cell = {
                id: header.id
            };

            if (header.hasOwnProperty("action")) {
                cell.handleClick = (_: Event, row: Cell) => {
                    if (header.action != null) {
                        header.action(row.id)
                    }
                };
            }

            cell.align = header.align;
            cell.clickable = header.clickable;

            cells.push(cell);
        });

        return cells;
    }

    function renderData() {
        const headers = createHeaders();
        const cells = createCells(headers);


        return (
            searchedData.length === 0 ?
                <PagePlaceholder
                    image={getIcon("add-file.svg")}
                    text={t('targeting-content.none-found')}
                /> :
                <SimpleTable
                    isSelectable
                    selected={selected}
                    handleSelection={setSelected}
                    dataId={"id"}
                    cells={cells}
                    headers={headers}
                    rows={searchedData}
                />
        )
    }

    function renderDeleteContentModal() {
        return (
            <DeleteModal
                data={formattedSelected}
                open={deleteFileModalOpen}
                handleDone={handleConfirmDelete}
                handleCancel={handleCancelDelete}
            />
        )
    }

    return (
        <Box>
            <HeaderBlock

                title={t('targeting-content.content')}
                right={
                    <Grid container spacing={2} alignItems="center" justify="flex-end">
                        <Grid item>
                            <Search
                                searchField={"name"}
                                secondaryFields={["id", "type"]}
                                returnSearchMatches={setSearchedData}
                                data={originalData}
                                searchTitle={t('targeting-content.search')}
                            />
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Button
                                    disabled={selected.length == 0}
                                    variant="outlined"
                                    color="primary"
                                    onClick={setDeleteFileModalOpenTrue}
                                >
                                    {t('common.delete')}
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={setFileModalOpenTrue}
                                >
                                    {t('common.add')}
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                }
            />

            {renderDeleteContentModal()}

            <FileDropModal
                title={t('targeting-content.upload')}
                open={addFileModalOpen}
                message={t('targeting-content.add')}
                handleDone={handleFileUploadDone}
                handleCancel={setFileModalOpenFalse}
                enforceTypes={contentTypeList}
                enforceTypesMessage={t('targeting-content.not-accepted')}
                fileType={contentTypeList[0].key}
                hasTags
            />

            <PageContainer hasFooter>
                {loading ? <Loading /> : renderData()}
            </PageContainer>
        </Box>
    )
}

export default ListContentPage